@if (hdKeynoteVisible()) {
  <div
    class="hd-keynote-card"
    [class.hd-keynote-card--with-notch]="showUndefinedNotch()"
    [class.hd-keynote-card--compact]="useCompactHdKeynoteText()"
    [class.hd-keynote-card--forced-break]="useForcedHdKeynoteBreak()"
    data-test-id="planet-keynote-card"
    role="tooltip"
    [id]="hdKeynoteId"
    [style.width.px]="80"
    [style.background-color]="backgroundColor()"
    [style.color]="defaultTextColor()"
    [style.border]="keynoteBorder()"
    [style.opacity]="opacity()"
  >
    @if (showUndefinedNotch()) {
      <div
        class="undefined-corner-notch"
        aria-hidden="true"
        [style.--undefined-notch-color]="colorConfig().undefinedNotchColor"
      ></div>
    }

    @if (showUndefinedHatch()) {
      <div
        class="undefined-hatch-overlay"
        aria-hidden="true"
        [style.--undefined-hatch-color]="hatchColor()"
      ></div>
    }

    <span class="hd-keynote-card__text">
      @for (line of hdKeynoteDisplayLines(); track $index; let isLast = $last) {
        <span class="hd-keynote-card__line" data-test-id="planet-keynote-line">{{ line }}</span>
        @if (!isLast) {
          <br>
        }
      }
    </span>
  </div>
}

<div
  class="planet-block"
  [class.undefined]="!isDefined()"
  [class.dark-bg]="isDarkBackground()"
  [class.highlighted]="isLineHighlighted()"
  [class.inverse]="isInverseStyle()"
  [class.has-node-role-icon]="nodeRoleIconClass() !== null"
  [class.resonance]="showResonanceKeynote()"
  [style.background-color]="backgroundColor()"
  [style.opacity]="opacity()"
  [style.border]="blockBorder()"
>
  @if (showUndefinedBorder()) {
    <div class="undefined-border" [style.border-color]="colorConfig().undefinedInnerBorderColor"></div>
  }

  @if (showUndefinedNotch()) {
    <div
      class="undefined-corner-notch"
      [style.--undefined-notch-color]="colorConfig().undefinedNotchColor"
    ></div>
  }

  @if (showUndefinedHatch()) {
    <div
      class="undefined-hatch-overlay"
      [style.--undefined-hatch-color]="hatchColor()"
    ></div>
  }

  <div class="planet-icon" [style.color]="defaultTextColor()">
    {{ displayConfig().astronomiconChar }}
  </div>

  <div class="fixing-badges" [class.no-base]="!showBaseBadge() && !echoBaseBadge()" [class.resonance-base]="showResonanceBase()" [class.dual-fixing]="showBothFixingBadge()">
    @if (showExaltationBadge()) {
      <div class="fixing-badge exaltation" [style.background-color]="colorConfig().exaltation">
        <span class="fixing-arrow" [style.color]="colorConfig().exaltationArrow">▲</span>
      </div>
    }
    @if (showDetrimentBadge()) {
      <div class="fixing-badge detriment" [style.background-color]="colorConfig().detriment">
        <span class="fixing-arrow" [style.color]="colorConfig().detrimentArrow">▼</span>
      </div>
    }
  </div>

  @if (showBaseBadge()) {
    <div
      class="base-badge"
      [class.not-full]="!isBaseFull()"
      [class.contrast-border]="baseBadgeNeedsContrastBorder()"
      [style.--base-q]="baseBadgeQuadrantColor()"
      (mouseenter)="onBaseBadgeEnter($event)"
      (mouseleave)="onBaseBadgeLeave($event)"
    >
      <div class="quadrant" [class.filled]="baseBadgeState().tl"></div>
      <div class="quadrant" [class.filled]="baseBadgeState().tr"></div>
      <div class="quadrant" [class.filled]="baseBadgeState().bl"></div>
      <div class="quadrant" [class.filled]="baseBadgeState().br"></div>
      <span class="base-number" [class.full-bg]="isBaseFull()">{{ activation().base }}</span>
    </div>
  }

  @if (echoBaseBadge(); as echo) {
    <div
      class="base-badge echo-base-badge"
      [class.not-full]="!isEchoBaseFull()"
      [class.contrast-border]="echoBaseBadgeNeedsContrastBorder()"
      [style.--base-q]="echo.quadrantColor"
    >
      <div class="quadrant" [class.filled]="echo.state.tl"></div>
      <div class="quadrant" [class.filled]="echo.state.tr"></div>
      <div class="quadrant" [class.filled]="echo.state.bl"></div>
      <div class="quadrant" [class.filled]="echo.state.br"></div>
      <span class="base-number" [class.full-bg]="isEchoBaseFull()">{{ echo.base }}</span>
    </div>
  }

  @if (visibleGeneKeysLabel(); as geneKeys) {
    <div class="gene-keys-label" [style.color]="defaultTextColor()">
      {{ geneKeys }}
    </div>
  }

  @if (nodeRoleIconClass(); as iconClass) {
    <i
      [class]="'node-role-icon ' + iconClass"
      [class.is-prime]="nodeRole() === 'prime'"
      [class.is-link]="nodeRole() === 'link'"
      [style.color]="defaultTextColor()"
      [title]="nodeRoleTooltip()"
      aria-hidden="true"
      (mouseenter)="onLinkIconEnter($event)"
      (mouseleave)="onLinkIconLeave($event)"
    ></i>
  }

  <div
    class="gate-number"
    [class.align-right]="colorConfig().gateNumberAlign === 'right'"
    [class.size-medium]="colorConfig().gateNumberFontSize === 'medium'"
    [style.color]="defaultTextColor()"
  >
    <span class="gate-value">{{ activation().gate }}</span>
    <span class="separator">.</span>
    <span
      class="line-value"
      [style.color]="lineColor()"
      [style.text-shadow]="lineShadow()"
    >
      {{ activation().line }}
    </span>

    @if (showColorNumber()) {
      <span class="separator">.</span>
      <span
        class="color-value"
        [style.color]="colorNumberColor()"
        [style.text-shadow]="colorNumberShadow()"
      >
        {{ activation().color }}
      </span>
    }
  </div>

  @if (showResonanceBase()) {
    <div
      class="resonance-base-symbol"
      [class.top-left]="resonanceBasePlacement() === 'top-left'"
      [class.resonance-base-symbol--icon]="resonanceBaseIconSrc() !== null"
      [style.color]="defaultTextColor()"
      [title]="resonanceBaseTooltip() ?? ''"
      data-test-id="resonance-base-symbol"
    >
      @if (resonanceBaseIconSrc(); as iconSrc) {
        <img class="resonance-base-symbol__icon" [src]="iconSrc" alt="" aria-hidden="true" />
      } @else {
        {{ resonanceBaseNumber() }}
      }
    </div>
  }

  @if (showResonanceKeynote()) {
    <div
      class="resonance-keynote"
      [style.color]="defaultTextColor()"
      [title]="resonanceKeynoteTooltip() ?? ''"
      data-test-id="resonance-keynote"
    >
      {{ resonanceKeynote() }}
    </div>
  }
</div>
